<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'GenerateMeterConsumptions',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    formId: {
      type: String,
      required: false,
      default: function () {
        return uniqueID();
      },
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const labelAlign = ref('right');
  const formName = ref('GenerateMeterConsumptions');
  const item = ref({
    min_date: null,
    max_date: null,
    meters: null,
    contracts: null,
    rentals: null,
    companies: null,
    tenants: null,
    exact_period: false,
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    formName: formName.value,
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'formName' },
      {
        name: 'item',
        cache: { type: 'local', key: 'GenerateMeterConsumptions.item' },
      },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      labelAlign,
      formName,
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'GenerateMeterConsumptions|Title',
        'Generate Consumptions'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.formId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('formId');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_vw0n8l4n3_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_8564662037462133_closeComponent_05h0ja3y8_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        cancel: true,
      },
    };
  };
  const act_4186377094414664_closeComponent_3lc0q5il9_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        ok: true,
        value: item.value,
      },
    };
  };

  const act_7315092382398562_closeModal_vw0n8l4n3 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_vw0n8l4n3_getActionArgs,
    });
  const inst_evh_7315092382398562_close_7j61gtccm =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_vw0n8l4n3,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_7j61gtccm],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_otve0z9cq =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_grgq0yeo9 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_otve0z9cq,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_grgq0yeo9],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_05h0ja3y8 =
    new actionTypes.CloseComponentAction({
      actionArgs: act_8564662037462133_closeComponent_05h0ja3y8_getActionArgs,
    });
  const inst_evh_8564662037462133_close_4ganoes0i =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_05h0ja3y8,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_4ganoes0i],
    event: 'close',
  });
  const act_4186377094414664_closeComponent_3lc0q5il9 =
    new actionTypes.CloseComponentAction({
      actionArgs: act_4186377094414664_closeComponent_3lc0q5il9_getActionArgs,
      displayName: 'submit',
    });
  const inst_evh_4186377094414664_submit_gvqpfm4qf =
    new actionTypes.EventHandlerImpl({
      action: act_4186377094414664_closeComponent_3lc0q5il9,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_4186377094414664_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4186377094414664_submit_gvqpfm4qf],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_tnvh29qtw =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_bhrt26ij7 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_tnvh29qtw,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_bhrt26ij7],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_7j61gtccm,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_grgq0yeo9,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_4ganoes0i,
    inst_evh_8564662037462133_close,
    inst_evh_4186377094414664_submit_gvqpfm4qf,
    submit: inst_evh_4186377094414664_submit_gvqpfm4qf,
    inst_evh_4186377094414664_submit,
    inst_evh_2434514588044777_clickPreventStop_bhrt26ij7,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_7j61gtccm = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_7j61gtccm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_grgq0yeo9 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_grgq0yeo9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_4ganoes0i = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_4ganoes0i.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_submit_gvqpfm4qf = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_submit_gvqpfm4qf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_submit = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_bhrt26ij7 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_bhrt26ij7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="GenerateMeterConsumptions"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpAlert variant="info" show>
        <p>
          <ItpIcon fa="info-circle"> </ItpIcon>
          <ItpText wrap> Generate consumptions from readings. </ItpText>
        </p>
      </ItpAlert>
      <ItpForm
        :id="formId"
        :name="formName"
        ref="mainForm"
        @submit="evh_4186377094414664_submit($event, {})"
      >
        <ItpFormGroup
          name="min_date"
          :label="pgettext('GenerateMeterConsumptions', 'Start date')"
          :labelAlign="labelAlign"
          :label-cols-md="3"
          :content-cols-md="4"
          required
        >
          <ItpFormDatetime
            name="min_date"
            type="date"
            v-model="item.min_date"
            required
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="max_date"
          :label="pgettext('GenerateMeterConsumptions', 'End date')"
          :labelAlign="labelAlign"
          :label-cols-md="3"
          :content-cols-md="4"
          required
        >
          <ItpFormDatetime
            name="max_date"
            type="date"
            v-model="item.max_date"
            required
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="exact_period"
          :label="pgettext('GenerateMeterConsumptions', 'Exact period')"
          :labelAlign="labelAlign"
          :label-cols-md="3"
          :content-cols-md="9"
        >
          <ItpFormCheckbox
            name="exact_period"
            inline
            v-model="item.exact_period"
            :value="item.exact_period"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.exact_period =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :readonly="item.isReadOnly"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('GenerateMeterConsumptions|Submit', 'Generate')"
          :form="formId"
          type="submit"
          variant="primary"
          icon="fa-save"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Cancel')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
